import { apiFetch } from './client'

// ── Types ─────────────────────────────────────────────────────────────────────

/** A single line in the server-side cart. */
export interface ServerCartItem {
  normalized_mpn: string
  slug: string
  display_title: string
  unit_price: string | null
  line_subtotal: string | null
  quantity: number
  moq: number
  primary_image_url: string | null
  lead_time_days: number | null
}

/** Full cart response from GET /api/v1/cart */
export interface ServerCart {
  uuid: string
  items: ServerCartItem[]
  subtotal_ex_vat: string
  vat_amount: string
  total_inc_vat: string
  item_count: number
  has_unpriced_items: boolean
  updated_at: string
}

// ── API calls ─────────────────────────────────────────────────────────────────

/** Fetch the current cart (creates an empty one server-side if none exists). */
export async function fetchCart(): Promise<ServerCart> {
  const res = await apiFetch<{ data: ServerCart }>('/cart')
  return res.data
}

/** Add a product to the cart (or increment its quantity). */
export async function addCartItem(normalizedMpn: string, quantity: number): Promise<ServerCart> {
  const res = await apiFetch<{ data: ServerCart }>('/cart/items', {
    method: 'POST',
    body: JSON.stringify({ normalizedMpn, quantity }),
  })
  return res.data
}

/** Update the quantity of an existing cart item. Use quantity=0 to remove. */
export async function updateCartItem(normalizedMpn: string, quantity: number): Promise<ServerCart> {
  const res = await apiFetch<{ data: ServerCart }>(`/cart/items/${encodeURIComponent(normalizedMpn)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/merge-patch+json' },
    body: JSON.stringify({ quantity }),
  })
  return res.data
}

/** Remove a single item from the cart. */
export async function removeCartItem(normalizedMpn: string): Promise<void> {
  await apiFetch<void>(`/cart/items/${encodeURIComponent(normalizedMpn)}`, {
    method: 'DELETE',
  })
}

/** Empty the entire cart. */
export async function clearCartRemote(): Promise<void> {
  await apiFetch<void>('/cart', {
    method: 'DELETE',
  })
}
